import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { get, noop, uniq } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { clearConsumerInformation, clearOutboundTicketId, setConsumerInfo } from 'state/outboundTicket'
import { setSalesOrderId, clearSalesOrderId } from 'state/salesOrder'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import { Divider, Stack, Grid } from '@mui/material'
import { useGetSalesOrdersQuery } from 'services/salesOrders'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { calculateTotalWeight } from 'utils/data-manipulation/common'
import { getColumns } from 'components/generic/datagrid/utils'
import { salesOrderFields } from 'components/containers/contracts/sales-orders/salesOrdersHelpers'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import Chip from 'components/generic/chip/Chip'
import { DECIMAL_PRECISION_PRESETS } from 'utils/constants'
import { setDecimalPrecision, convertUM } from 'utils/helpers'

const LinkSalesOrder = ({ onFormStateChange, onHideModal }) => {
  const { data, isLoading } = useGetSalesOrdersQuery({})
  const dispatch = useDispatch()
  const { disableStatusFilter } = useSelector(state => state.ui.formModalProps)
  const { salesOrder } = useSelector(state => state.outboundTicket.consumerInfo)
  const { salesOrderId: currentSalesOrderId, filters } = useSelector(state => state.salesOrder)
  const { defaultYardUm } = useSelector(state => state.settings)
  const [activeFilters, setActiveFilters] = useState([])

  useEffect(() => {
    const newActiveFilters = Object.keys(filters)
      .filter(key => (disableStatusFilter && key === 'status' ? false : !!filters[key].value))
      .map(filter => filters[filter])
    setActiveFilters(newActiveFilters)
  }, [filters, disableStatusFilter])

  const handleLink = consumerInfo => {
    const { salesOrderId, salesOrderNumber } = consumerInfo
    let value = ''

    if (parseInt(salesOrderNumber, 10) !== salesOrder) {
      value = salesOrderNumber
    }
    dispatch(clearOutboundTicketId())
    if (salesOrderId !== currentSalesOrderId) {
      dispatch(setSalesOrderId(salesOrderId))
      dispatch(setConsumerInfo({ ...consumerInfo, salesOrder: value }))
    } else {
      dispatch(clearSalesOrderId())
      dispatch(clearConsumerInformation())
    }

    onHideModal()
  }

  const transformedRows = (data || [])
    .slice()
    .sort((a, b) => {
      if (a.salesOrderTicketId?.toString() === salesOrder) return -1
      if (b.salesOrderTicketId?.toString() === salesOrder) return 1
      return new Date(b?.updatedAt) - new Date(a?.updatedAt)
    })
    .filter(row => activeFilters.every(filter => get(row, filter.path) === filter.value))
    .map(ticket => {
      const materials = ticket.priceSheet.entriesObj
      const hasMultipleWeightUnits = uniq(materials.map(m => m.weightUnits?.toUpperCase())).length > 1
      const um = (materials?.[0]?.weightUnits || defaultYardUm).toUpperCase()

      const totalWeight = calculateTotalWeight(materials)
      const convertedFulfilled = convertUM(ticket.fulfilledAmount, defaultYardUm, um)

      return {
        id: ticket.id,
        readableId: ticket.salesOrderTicketId,
        salesOrderNumber: ticket.salesOrderTicketId,
        customerOrderNumber: ticket.customerPONumber,
        effectiveDate: dateFormatter(ticket.orderSalesDetails.effectiveDate),
        customerName: ticket.customer.displayName,
        materials,
        ordered: hasMultipleWeightUnits
          ? 'Mixed'
          : `${setDecimalPrecision(totalWeight, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
        remain: hasMultipleWeightUnits
          ? 'Mixed'
          : `${setDecimalPrecision(totalWeight - convertedFulfilled, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
        invoiceWeights: ticket.orderSalesDetails.invoiceWeights,
        customer: {
          id: ticket.customer.id,
          customerCommonIdentifierString: ticket.customer.displayName,
          companyName: ticket.customer.companyName,
        },
        isLinked: {
          linkedId: salesOrder || '',
          onLink: handleLink,
        },
      }
    })

  const columns = [
    'isLinked',
    'salesOrderNumber',
    'customerOrderNumber',
    'effectiveDate',
    'customerName',
    'materials',
    'ordered',
    'remaining',
  ]

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  return (
    <Stack>
      <FormModalHeading title="Link Sales Order" backButtonPosition="right" onBack={() => onHideModal()} />
      <Divider />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <DataGridWithSearch
          rows={transformedRows}
          loading={isLoading}
          columns={getColumns(columns, salesOrderFields)}
          textSearchPlaceholder="Search Sales Orders"
          showFilterChips
          filterChips={
            <>
              {activeFilters.map((filter, index) => (
                <Chip
                  key={filter?.name}
                  label={`${filter?.name}: ${filter?.value}`}
                  onDelete={
                    filter?.name === 'Status'
                      ? undefined
                      : () => {
                          const newActiveFilters = activeFilters.slice()
                          newActiveFilters.splice(index, 1)
                          setActiveFilters(newActiveFilters)
                        }
                  }
                />
              ))}
            </>
          }
        />
      </Grid>
    </Stack>
  )
}

LinkSalesOrder.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

LinkSalesOrder.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default LinkSalesOrder
